import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { useEffect } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Code as CodeIcon,
  Code2,
  Heading1,
  Heading2,
  Link as LinkIcon,
  Minus,
} from 'lucide-react';
import { cn } from '@shared';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /**
   * Render the editor with a minimum height. Useful inside dialogs where the
   * default tiptap height would feel cramped.
   */
  minHeight?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  minHeight = '120px',
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Write something...',
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Tiptap only reads `content` on mount. When the parent switches the value
  // (e.g. user opens a different task), push the new content into the editor
  // so the rendered HTML matches what the parent holds.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current === value) return;
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn('rich-text-editor rounded-md border border-input bg-background', className)}>
      <style>{`
        .rich-text-editor p { font-size: 14px; line-height: 1.5; margin: 0 0 0.5rem 0; }
        .rich-text-editor h1 { font-size: 32px; font-weight: 600; line-height: 1.2; margin: 0; }
        .rich-text-editor h2 { font-size: 24px; font-weight: 600; line-height: 1.3; margin: 0; }
        .rich-text-editor a { color: hsl(var(--primary)); text-decoration: underline; text-underline-offset: 2px; }
        .rich-text-editor a:hover { color: hsl(var(--primary-hover)); }
        .rich-text-editor ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        .rich-text-editor ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
        .rich-text-editor li { margin: 0.25rem 0; }
        .rich-text-editor li > p { margin: 0; }
        .rich-text-editor code { background: hsl(var(--muted)); padding: 0.1em 0.35em; border-radius: 0.25rem; font-size: 0.9em; }
        .rich-text-editor pre { background: hsl(var(--muted)); padding: 0.75rem 1rem; border-radius: 0.5rem; overflow-x: auto; margin: 0.5rem 0; }
        .rich-text-editor pre code { background: transparent; padding: 0; font-size: 0.85em; }
        .rich-text-editor hr { border: 0; border-top: 1px solid hsl(var(--border)); margin: 0.75rem 0; }
      `}</style>
      <div className="flex flex-wrap items-center gap-1 border-b p-2">
        <ToolbarButton
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          icon={Bold}
          label="Bold"
        />
        <ToolbarButton
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          icon={Italic}
          label="Italic"
        />
        <ToolbarSeparator />
        <ToolbarButton
          isActive={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          icon={Heading1}
          label="Heading 1"
        />
        <ToolbarButton
          isActive={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          icon={Heading2}
          label="Heading 2"
        />
        <ToolbarSeparator />
        <ToolbarButton
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          icon={List}
          label="Bullet list"
        />
        <ToolbarButton
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          icon={ListOrdered}
          label="Ordered list"
        />
        <ToolbarSeparator />
        <ToolbarButton
          isActive={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          icon={CodeIcon}
          label="Inline code"
        />
        <ToolbarButton
          isActive={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          icon={Code2}
          label="Code block"
        />
        <ToolbarSeparator />
        <LinkButton editor={editor} />
        <ToolbarButton
          isActive={false}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          icon={Minus}
          label="Horizontal rule"
        />
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-3 outline-none [&_.ProseMirror]:min-h-[var(--editor-min-h)] [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:m-0 [&_.ProseMirror_p]:leading-relaxed [&_.ProseMirror-focused]:outline-none"
        style={{ ['--editor-min-h' as string]: minHeight }}
        data-testid="rich-text-editor"
      />
    </div>
  );
}

function ToolbarButton({
  onClick,
  isActive,
  icon: Icon,
  label,
}: {
  onClick: () => void;
  isActive: boolean;
  icon: React.ElementType;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded p-1.5 transition-colors',
        isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted',
      )}
      title={label}
      aria-label={label}
      aria-pressed={isActive}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="mx-1 h-5 w-px bg-border" aria-hidden />;
}

function LinkButton({ editor }: { editor: Editor }) {
  const isActive = editor.isActive('link');
  const handleClick = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter URL', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'rounded p-1.5 transition-colors',
        isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted',
      )}
      title="Link"
      aria-label="Link"
      aria-pressed={isActive}
    >
      <LinkIcon className="h-4 w-4" />
    </button>
  );
}
